import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Blog7 — Centered blog card grid.
 * Based on the shadcnblocks.com Blog7 block pattern.
 *
 * Props:
 *   tagline      - small uppercase label
 *   heading      - main heading
 *   description  - supporting copy
 *   buttonText   - CTA label
 *   buttonUrl    - CTA link
 *   posts        - [{ id, title, summary, label, author, published, url, image }]
 */
export function Blog7({
  tagline = "Latest Updates",
  heading = "Blog Posts",
  description,
  buttonText,
  buttonUrl,
  posts = [],
}) {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="mx-auto max-w-2xl text-center mb-12">
          <h2 className="text-base text-primary font-semibold tracking-wide uppercase">
            {tagline}
          </h2>
          <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
            {heading}
          </p>
          {description && (
            <p className="mt-4 text-xl text-gray-500">
              {description}
            </p>
          )}
        </div>

        {/* Blog card grid */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <article
              key={post.id}
              className="group flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              {/* Image */}
              <Link
                to={post.url}
                className="relative block aspect-video overflow-hidden bg-gray-100"
              >
                {post.image ? (
                  <img
                    src={post.image}
                    alt={post.title}
                    className="size-full object-cover transition-opacity duration-300 group-hover:opacity-90"
                  />
                ) : (
                  <div className="size-full bg-gradient-to-br from-primary/10 to-blue-400/10" />
                )}
              </Link>

              {/* Body */}
              <div className="flex flex-1 flex-col p-6">
                {/* Label badge */}
                {post.label && (
                  <span className="mb-3 inline-flex w-fit items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    {post.label}
                  </span>
                )}

                {/* Title */}
                <h3 className="text-lg font-medium text-gray-900">
                  <Link
                    to={post.url}
                    className="underline-offset-4 hover:underline"
                  >
                    {post.title}
                  </Link>
                </h3>

                {/* Author + date */}
                {(post.author || post.published) && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                    {post.author && <span>{post.author}</span>}
                    {post.author && post.published && (
                      <span className="text-gray-300">•</span>
                    )}
                    {post.published && <span>{post.published}</span>}
                  </div>
                )}

                {/* Summary */}
                {post.summary && (
                  <p className="mt-3 text-gray-500">{post.summary}</p>
                )}

                {/* Read more */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <Link
                    to={post.url}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                  >
                    Read more
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* CTA button */}
        {buttonText && buttonUrl && (
          <div className="mt-12 text-center">
            <Link
              to={buttonUrl}
              className="inline-flex items-center gap-2 px-6 py-3 border border-primary text-base font-medium rounded-md text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300"
            >
              {buttonText}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default Blog7;
